import { PRO_PRICE_PER_MONTH } from "~/utils/pricing";
import { useMemo, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import {
  AppLink,
  AppText,
  BrandMark,
  Button,
  ButtonText,
  Card,
  Row,
  ScrollScreen,
  Stack,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import type { Tables } from "~/api/tables";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";

type Invitation = Tables<"workspace_invitations">;

export default function InvitationScreen() {
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const router = useRouter();
  const token =
    (Array.isArray(params.token) ? params.token[0] : params.token) ?? "";
  const { isAuthenticated, loading, updatePreferences } = useAuth();
  const [answering, setAnswering] = useState<"accept" | "decline" | null>(null);
  const [answered, setAnswered] = useState<Invitation | null>(null);
  const [error, setError] = useState("");
  const invitationPath = useMemo(
    () => `/invitations?token=${encodeURIComponent(token)}`,
    [token],
  );

  async function answer(action: "accept" | "decline") {
    if (!token || answering) return;
    setAnswering(action);
    setError("");
    try {
      const invitation = await unwrap(
        api.respondToWorkspaceInvitation({ body: { token, action } }),
      );
      setAnswered(invitation as unknown as Invitation);
      if (action === "accept") {
        await updatePreferences({ activeWorkspaceId: invitation.workspace_id });
      }
    } catch (invitationError) {
      setError(
        invitationError instanceof Error
          ? invitationError.message
          : "Unable to answer invitation.",
      );
    } finally {
      setAnswering(null);
    }
  }

  return (
    <ScrollScreen>
      <BrandMark />
      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Workspace invitation</AppText>
          <AppText size="xs">
            Join a MintStint workspace. Accountant seats are free and strictly
            view-only. Working seats are included in the owner's{" "}
            {PRO_PRICE_PER_MONTH}
            plan.
          </AppText>
        </Stack>
        {!token ? (
          <AppText tone="danger">This invitation link is invalid.</AppText>
        ) : null}
        {token && loading ? (
          <AppText size="xs">Checking your session...</AppText>
        ) : null}
        {token && !loading && !isAuthenticated ? (
          <Stack gap="sm">
            <AppText size="xs">
              Sign in or register with the email address that received this
              invitation.
            </AppText>
            <AppLink
              href={
                `/login?callbackURL=${encodeURIComponent(invitationPath)}` as never
              }
            >
              Sign in to continue
            </AppLink>
            <AppLink
              href={
                `/register?callbackURL=${encodeURIComponent(invitationPath)}` as never
              }
            >
              Create an account
            </AppLink>
          </Stack>
        ) : null}
        {token && !loading && isAuthenticated && !answered ? (
          <Stack gap="sm">
            {error ? <AppText tone="danger">{error}</AppText> : null}
            <Row>
              <Button
                disabled={Boolean(answering)}
                onPress={() => void answer("accept")}
              >
                <ButtonText>
                  {answering === "accept" ? "Accepting..." : "Accept invite"}
                </ButtonText>
              </Button>
              <Button
                variant="outline"
                disabled={Boolean(answering)}
                onPress={() => void answer("decline")}
              >
                <ButtonText>
                  {answering === "decline" ? "Declining..." : "Decline"}
                </ButtonText>
              </Button>
            </Row>
          </Stack>
        ) : null}
        {answered ? (
          <Stack gap="sm">
            <AppText>
              {answered.status === "accepted"
                ? "Invitation accepted. Workspace access is ready."
                : "Invitation declined."}
            </AppText>
            {answered.status === "accepted" ? (
              <Button onPress={() => router.replace("/dashboard" as never)}>
                <ButtonText>Open dashboard</ButtonText>
              </Button>
            ) : null}
          </Stack>
        ) : null}
        <AppLink href={isAuthenticated ? "/dashboard" : "/login"}>Back</AppLink>
      </Card>
    </ScrollScreen>
  );
}
